import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Eye, Lock, Users, Globe, Database, FileText, Building, UserCheck, Clock, Ban } from 'lucide-react';
import '../../styles/pages/privacy-policy.css';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="legal-page privacy-policy-page">
      {/* Header */}
      <div className="topbar">
        <div className="topbar-inner">
          <div className="topbar-row">
            <a
              href="https://smarteco.ai/"
              target="_blank"
              rel="noopener noreferrer"
              className="logo-link"
            >
              <img
                src={`${process.env.PUBLIC_URL}/SVGIcons/SmartecoLogo.svg`}
                alt="Logo"
                className="logo smarteco-logo"
              />
            </a>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="main">
        <div className="card">
          <div className="title-block">
            <h1 className="title">Privacy Policy – SmartEco Mobile App</h1>
            <p className="subtitle">Last updated: November 26, 2025</p>
            <p className="subtitle">Effective Date: November 26, 2025</p>
          </div>

          {/* Data Controller */}
          <section className="section boxed">
            <h2>
              <Building />
              Data Controller
            </h2>
            <p>
              The <strong>Data Controller</strong> for personal information collected through SmartEco is:
            </p>
            <div className="callout">
              <p className="name">iSpatial Techno Solutions LLC</p>
              <p>
                <strong>Address:</strong> iSpatial Techno Solutions LLC, Abu Dhabi, UAE<br />
                <strong>Email:</strong> <a href="mailto:smartecosupport@ispatialtec.com">smartecosupport@ispatialtec.com</a><br />
                <strong>Phone:</strong> +971 50 582 5503
              </p>
            </div>
          </section>

          {/* DPO */}
          <section className="section boxed">
            <h2>
              <UserCheck />
              Data Protection Officer (DPO)
            </h2>
            <p>
              If you have any privacy concerns or questions, you can contact our Data Protection Officer:
            </p>
            <div className="callout">
              <p>
                <strong>DPO Email:</strong> <a href="mailto:smartecosupport@ispatialtec.com">smartecosupport@ispatialtec.com</a><br />
                <strong>Subject Line:</strong> &quot;Privacy Inquiry - SmartEco App&quot;
              </p>
            </div>
          </section>

          {/* Overview */}
          <section className="section">
            <h2>
              <Eye />
              1. Overview
            </h2>
            <p>
              SmartEco is a <strong>WebView-based mobile application</strong> that displays the SmartEco web dashboard. The mobile app itself acts as a container and does not independently collect, store, or process personal data.
            </p>
            <p>
              However, when you use features within the web dashboard (displayed inside the app), personal data may be collected and processed by the SmartEco web service. This policy covers both the mobile app and the web dashboard services.
            </p>
            <div className="info-box">
              <p>
                <strong>Dashboard URL:</strong> <a href="https://smarteco.ai" target="_blank" rel="noopener noreferrer">https://smarteco.ai</a>
              </p>
            </div>
          </section>

          {/* What Data We Collect */}
          <section className="section">
            <h2>
              <Database />
              2. What Personal Data We Collect
            </h2>
            <p>
              The SmartEco web dashboard (accessed through the app) may collect the following types of personal data <strong>with your explicit permission:</strong>
            </p>

            <div className="stack lg">
              <div className="info-box">
                <p className="label">Account Information:</p>
                <ul className="dot">
                  <li>Email address</li>
                  <li>Username</li>
                  <li>Password (encrypted)</li>
                  <li>Account preferences</li>
                </ul>
              </div>

              <div className="info-box">
                <p className="label">Location Data (with your consent):</p>
                <ul className="dot">
                  <li>Device location address</li>
                  <li>Geographic coordinates</li>
                  <li>Registered device installation locations</li>
                </ul>
              </div>

              <div className="info-box">
                <p className="label">Network Information (with your consent):</p>
                <ul className="dot">
                  <li>WiFi network list (nearby WiFi networks)</li>
                  <li>Nearby hotspot devices</li>
                  <li>Network names (SSIDs) - <strong>passwords are NEVER accessed</strong></li>
                </ul>
              </div>

              <div className="info-box">
                <p className="label">Air Quality Device Data:</p>
                <ul className="dot">
                  <li>Device configuration and settings</li>
                  <li>Air quality readings and sensor data</li>
                  <li>Device status and diagnostics</li>
                  <li>Activity logs and usage patterns</li>
                </ul>
              </div>
            </div>

            <div className="info-box bordered" style={{ marginTop: '1rem' }}>
              <p className="label">Important:</p>
              <p>
                The SmartEco mobile app itself does <strong>NOT</strong> collect: analytics data, advertising IDs, photos, files, contacts, phone calls, SMS messages, camera access, microphone access, or any other sensitive device permissions beyond what is explicitly stated above.
              </p>
            </div>
          </section>

          {/* Why We Process Data */}
          <section className="section">
            <h2>
              <FileText />
              3. Why We Process Your Personal Data (Purpose)
            </h2>
            <p>We collect and process your personal data for the following specific purposes:</p>

            <div className="stack">
              <div className="info-box bordered">
                <p className="label">✓ Account Management</p>
                <p>To create and manage your SmartEco account, authenticate users, and provide access to dashboard features.</p>
              </div>

              <div className="info-box bordered">
                <p className="label">✓ Air Quality Monitoring Services</p>
                <p>To provide real-time air quality data, device monitoring, and environmental analytics for your registered locations.</p>
              </div>

              <div className="info-box bordered">
                <p className="label">✓ Device Location Association</p>
                <p>To accurately link air quality monitoring devices to specific physical locations and provide location-based environmental data.</p>
              </div>

              <div className="info-box bordered">
                <p className="label">✓ Network-Based Device Discovery</p>
                <p>To identify and register SmartEco monitoring devices in your area using WiFi and hotspot information, improving device setup accuracy.</p>
              </div>

              <div className="info-box bordered">
                <p className="label">✓ Service Improvement</p>
                <p>To improve service quality, troubleshoot technical issues, and enhance user experience based on usage patterns.</p>
              </div>

              <div className="info-box bordered">
                <p className="label">✓ Alerts and Notifications</p>
                <p>To send air quality alerts, device status notifications, and important service updates related to your account.</p>
              </div>

              <div className="info-box bordered">
                <p className="label">✓ Legal Compliance</p>
                <p>To comply with applicable laws, regulations, and legal processes.</p>
              </div>
            </div>
          </section>

          {/* Data Sharing */}
          <section className="section">
            <h2>
              <Users />
              4. Data Sharing and Third-Party Disclosure
            </h2>

            <div className="nested-box">
              <p className="important lg">✓ We DO NOT Sell Your Personal Data</p>
              <p>
                SmartEco does <strong>NOT</strong> sell, rent, or trade your personal information to third parties for monetary or other valuable consideration.
              </p>
              <p>Your data is used solely to provide air quality monitoring services as described in this policy.</p>
            </div>

            <h3 style={{ fontWeight: 600, color: '#111827', margin: '0 0 0.75rem 0', fontSize: '1.125rem' }}>
              When We May Share Your Data:
            </h3>
            <p>We may share your personal data only in the following limited circumstances:</p>

            <div className="stack">
              <div className="info-box">
                <p className="label">1. Service Providers</p>
                <p>Cloud hosting providers and infrastructure services necessary to operate the SmartEco platform. These providers are contractually obligated to protect your data.</p>
              </div>

              <div className="info-box">
                <p className="label">2. Legal Requirements</p>
                <p>When required by law, court order, or government regulation, or to protect our legal rights.</p>
              </div>

              <div className="info-box">
                <p className="label">3. Business Transfers</p>
                <p>In the event of a merger, acquisition, or sale of assets, your data may be transferred to the acquiring entity.</p>
              </div>

              <div className="info-box">
                <p className="label">4. With Your Consent</p>
                <p>When you explicitly authorize us to share your data with specific third parties.</p>
              </div>
            </div>

            <div className="info-box bordered" style={{ marginTop: '1rem' }}>
              <p className="label">International Data Transfers:</p>
              <p>
                Your data may be processed on servers located outside your country of residence. We ensure appropriate safeguards are in place for international transfers in compliance with GDPR and other applicable regulations.
              </p>
            </div>
          </section>

          {/* Data Retention */}
          <section className="section">
            <h2>
              <Clock />
              5. Data Retention - How Long We Keep Your Data
            </h2>
            <p>We retain your personal data only as long as necessary to fulfill the purposes described in this policy:</p>

            <div className="stack">
              <div className="info-box">
                <p className="label">Active Account Data</p>
                <p><strong>Retention Period:</strong> While your account is active and for up to <strong>90 days</strong> after account deletion or deactivation.</p>
                <p className="meta">Includes: Account credentials, user preferences, device configurations</p>
              </div>

              <div className="info-box">
                <p className="label">Air Quality Data</p>
                <p><strong>Retention Period:</strong> <strong>24 months</strong> for historical analysis and trend monitoring.</p>
                <p className="meta">Includes: Sensor readings, device measurements, environmental data</p>
              </div>

              <div className="info-box">
                <p className="label">Location Data</p>
                <p><strong>Retention Period:</strong> While your account is active and for up to <strong>30 days</strong> after account deletion.</p>
                <p className="meta">Includes: Device location addresses, geographic coordinates</p>
              </div>

              <div className="info-box">
                <p className="label">WiFi and Network Data</p>
                <p><strong>Retention Period:</strong> <strong>30 days</strong> or until account deletion, whichever comes first.</p>
                <p className="meta">Includes: WiFi network lists, hotspot device information</p>
              </div>

              <div className="info-box">
                <p className="label">Legal and Compliance Records</p>
                <p><strong>Retention Period:</strong> Up to <strong>7 years</strong> as required by applicable law and regulations.</p>
                <p className="meta">Includes: Transaction records, audit logs for compliance purposes</p>
              </div>
            </div>

            <div className="info-box bordered" style={{ marginTop: '1rem' }}>
              <p className="label">Data Deletion:</p>
              <p>
                After the retention periods expire, your personal data will be securely deleted or anonymized so it can no longer be associated with you. You can request earlier deletion at any time (see Section 6).
              </p>
            </div>
          </section>

          {/* User Rights */}
          <section className="section">
            <h2>
              <Shield />
              6. Your Privacy Rights
            </h2>
            <p>Under GDPR, CCPA, VCDPA, POPIA, PIPA, and other privacy laws, you have the following rights:</p>

            <div className="stack">
              <div className="info-box bordered">
                <p className="label">✓ Right to Access Your Data</p>
                <p>You can request a copy of all personal data we hold about you. We will provide this information in a portable, machine-readable format.</p>
                <p className="meta"><strong>How to exercise:</strong> Email smartecosupport@ispatialtec.com with subject &quot;Data Access Request&quot;</p>
              </div>

              <div className="info-box bordered">
                <p className="label">✓ Right to Correct Inaccurate Data</p>
                <p>You can request correction of any inaccurate or incomplete personal data we hold about you.</p>
                <p className="meta"><strong>How to exercise:</strong> Update your information in the app settings or email smartecosupport@ispatialtec.com</p>
              </div>

              <div className="info-box bordered">
                <p className="label">✓ Right to Delete Your Data</p>
                <p>You can request deletion of your personal data, and we will comply within 30 days unless we have a legal obligation to retain it.</p>
                <p className="meta"><strong>How to exercise:</strong> Email smartecosupport@ispatialtec.com with subject &quot;Delete My Data&quot;</p>
              </div>

              <div className="info-box bordered">
                <p className="label">✓ Right to Know If Your Data Is Sold or Shared</p>
                <p>You have the right to know whether your personal data is sold or shared with third parties. <strong>We do NOT sell your data.</strong> Limited sharing occurs only as described in Section 4.</p>
                <p className="meta"><strong>How to exercise:</strong> Review Section 4 of this policy or email us for clarification</p>
              </div>

              <div className="info-box bordered">
                <p className="label">✓ Right to Opt-Out of Data Sale (CCPA)</p>
                <p><strong>We do NOT sell your personal data</strong>, so opt-out is not necessary. However, California residents can confirm this by contacting us.</p>
                <p className="meta"><strong>How to exercise:</strong> Email smartecosupport@ispatialtec.com</p>
              </div>

              <div className="info-box bordered">
                <p className="label">✓ Right to Opt-Out of Sharing (Targeted Advertising)</p>
                <p>We do NOT share your data for targeted advertising purposes. SmartEco does not use advertising networks or tracking technologies.</p>
              </div>

              <div className="info-box bordered">
                <p className="label">✓ Right to Restrict Processing</p>
                <p>You can request that we limit how we use your personal data while disputes are resolved.</p>
                <p className="meta"><strong>How to exercise:</strong> Email smartecosupport@ispatialtec.com with subject &quot;Restrict Processing&quot;</p>
              </div>

              <div className="info-box bordered">
                <p className="label">✓ Right to Withdraw Consent</p>
                <p>You can withdraw your consent for location, WiFi, or hotspot data collection at any time through device settings or by contacting us.</p>
                <p className="meta"><strong>How to exercise:</strong> Revoke permissions in device settings or email us</p>
              </div>

              <div className="info-box bordered">
                <p className="label">✓ Right to Object to Processing</p>
                <p>You can object to processing of your personal data for specific purposes such as direct marketing or legitimate interests.</p>
                <p className="meta"><strong>How to exercise:</strong> Email smartecosupport@ispatialtec.com</p>
              </div>
            </div>

            <div className="response-time">
              <p className="important">⏱ Response Time:</p>
              <p>We will respond to your rights requests within:</p>
              <ul className="dot">
                <li><strong>30 days</strong> for GDPR requests</li>
                <li><strong>45 days</strong> for CCPA/VCDPA requests (may extend to 90 days if complex)</li>
              </ul>
            </div>
          </section>

          {/* Managing Permissions */}
          <section className="section">
            <h2>
              <Ban />
              7. How to Opt-Out of Data Sale/Sharing and Targeted Advertising
            </h2>

            {/* Summary Box */}
            <div className="nested-box">
              <p className="summary-title">📢 Your Opt-Out Rights - Summary</p>
              <p className="lead">
                Under CCPA (California) and VCDPA (Virginia) laws, you have the right to opt-out of:
              </p>

              <div className="optout-grid">
                <div className="optout-card">
                  <p className="title">1️⃣ Sale of Personal Data</p>
                  <p className="meta">Status: <strong className="ok">We DO NOT sell data</strong></p>
                  <p className="meta"><strong>How to opt-out:</strong> Email smartecosupport@ispatialtec.com</p>
                </div>

                <div className="optout-card">
                  <p className="title">2️⃣ Targeted Advertising</p>
                  <p className="meta">Status: <strong className="ok">We DO NOT use ads</strong></p>
                  <p className="meta"><strong>How to opt-out:</strong> Email smartecosupport@ispatialtec.com</p>
                </div>

                <div className="optout-card">
                  <p className="title">3️⃣ Profiling (Legal Decisions)</p>
                  <p className="meta">Status: <strong className="ok">We DO NOT use profiling</strong></p>
                  <p className="meta"><strong>How to opt-out:</strong> Email smartecosupport@ispatialtec.com</p>
                </div>
              </div>

              <div className="callout">
                <p className="important">✅ Bottom Line:</p>
                <p>
                  SmartEco does <strong>NOT</strong> engage in any of these practices, so <strong>no action is required from you.</strong>
                </p>
                <p>
                  However, if you want written confirmation or wish to exercise these rights proactively, simply email <a href="mailto:smartecosupport@ispatialtec.com" className="strong">smartecosupport@ispatialtec.com</a> with your request. We will respond within <strong>15 business days</strong>.
                </p>
              </div>
            </div>

            {/* Opt-Out of Sale/Sharing */}
            <div className="nested-box">
              <p className="summary-title md">🚫 Opt-Out of Data Sale or Sharing</p>

              <div className="callout" style={{ marginBottom: '1rem' }}>
                <p className="important lg">Important Notice:</p>
                <p>
                  <strong>SmartEco does NOT sell your personal data to third parties.</strong>
                </p>
                <p>
                  <strong>SmartEco does NOT share your data for targeted advertising, cross-context behavioral advertising, or profiling purposes.</strong>
                </p>
                <p>
                  Therefore, <strong>opt-out is not necessary</strong> because these practices do not occur.
                </p>
              </div>

              <div className="info-box bordered" style={{ marginBottom: '1rem' }}>
                <p className="important">For California Residents (CCPA/CPRA):</p>
                <p>
                  Under California law, you have the right to opt-out of the &quot;sale&quot; or &quot;sharing&quot; of your personal information. Since we do NOT engage in these practices, this right does not apply.
                </p>
                <p>
                  If you would like written confirmation that we do not sell or share your data, email: <a href="mailto:smartecosupport@ispatialtec.com" className="strong">smartecosupport@ispatialtec.com</a> with subject &quot;CCPA Opt-Out Confirmation&quot;
                </p>
              </div>

              <div className="info-box bordered" style={{ marginBottom: '1rem' }}>
                <p className="important">For Virginia Residents (VCDPA):</p>
                <p>
                  Under the Virginia Consumer Data Protection Act (VCDPA), you have the right to opt-out of:
                </p>
                <ol className="num">
                  <li><strong>Targeted advertising</strong> - displaying ads based on personal data from multiple websites/apps</li>
                  <li><strong>Sale of personal data</strong> - exchanging personal data for monetary or valuable consideration</li>
                  <li><strong>Profiling</strong> - automated decisions that produce legal or similarly significant effects</li>
                </ol>
                <p>
                  <strong>SmartEco does NOT engage in any of these practices.</strong>
                </p>
                <p style={{ fontWeight: 600 }}>
                  However, if you wish to confirm or exercise these rights, email <a href="mailto:smartecosupport@ispatialtec.com">smartecosupport@ispatialtec.com</a> with your specific request.
                </p>
              </div>

              <div className="info-box bordered">
                <p className="important">How to Verify:</p>
                <p>You can verify our data practices by:</p>
                <ol className="num">
                  <li>Reviewing Section 4 (&quot;Data Sharing&quot;) of this policy</li>
                  <li>Contacting us at <a href="mailto:smartecosupport@ispatialtec.com" className="strong">smartecosupport@ispatialtec.com</a></li>
                  <li>Requesting a copy of your data to see how it has been used (Section 6)</li>
                </ol>
              </div>
            </div>

            {/* Opt-Out of Targeted Advertising */}
            <div className="nested-box">
              <p className="summary-title md">📢 Opt-Out of Targeted Advertising</p>

              <div className="callout" style={{ marginBottom: '1rem' }}>
                <p className="important lg">Current Practice:</p>
                <p>
                  <strong>SmartEco does NOT use your data for targeted advertising.</strong>
                </p>
                <p>We do NOT:</p>
                <ul className="dot spaced">
                  <li>Display advertisements in the app</li>
                  <li>Share your data with advertising networks</li>
                  <li>Use tracking pixels or advertising cookies</li>
                  <li>Profile you for marketing purposes</li>
                  <li>Sell data to data brokers or advertisers</li>
                </ul>
              </div>

              <div className="info-box bordered">
                <p className="important">How to Exercise This Right:</p>
                <p>If our practices change in the future, you can opt-out of targeted advertising by:</p>
                <ol className="num">
                  <li>Sending an email to <a href="mailto:smartecosupport@ispatialtec.com" className="strong">smartecosupport@ispatialtec.com</a></li>
                  <li>Using subject line: <strong>&quot;Opt-Out of Targeted Advertising&quot;</strong></li>
                  <li>We will process your request within <strong>15 business days</strong></li>
                </ol>
                <p style={{ fontWeight: 600 }}>
                  Current status: No opt-out action is needed because we do not engage in targeted advertising.
                </p>
              </div>
            </div>

            {/* Opt-Out of Profiling */}
            <div className="nested-box">
              <p className="summary-title md">🎯 Opt-Out of Profiling for Significant Decisions</p>

              <div className="callout" style={{ marginBottom: '1rem' }}>
                <p className="important lg">Current Practice:</p>
                <p>
                  <strong>SmartEco does NOT engage in profiling that produces legal or similarly significant consequences.</strong>
                </p>
                <p>
                  <strong>What is profiling?</strong> Automated processing of personal data to evaluate, analyze, or predict aspects concerning your behavior, preferences, or characteristics that could affect your rights.
                </p>
                <p>We do NOT:</p>
                <ul className="dot spaced">
                  <li>Make automated decisions that affect your legal rights</li>
                  <li>Use algorithms to deny services or benefits</li>
                  <li>Profile you for credit, employment, or insurance decisions</li>
                  <li>Create profiles for discriminatory purposes</li>
                  <li>Use AI/machine learning to make decisions with legal consequences</li>
                </ul>
              </div>

              <div className="info-box bordered">
                <p className="important">How to Exercise This Right:</p>
                <p>If our practices change in the future, you can opt-out of profiling by:</p>
                <ol className="num">
                  <li>Sending an email to <a href="mailto:smartecosupport@ispatialtec.com" className="strong">smartecosupport@ispatialtec.com</a></li>
                  <li>Using subject line: <strong>&quot;Opt-Out of Profiling&quot;</strong></li>
                  <li>Specify which profiling activities you wish to opt-out from</li>
                  <li>We will process your request within <strong>15 business days</strong></li>
                </ol>
                <p style={{ fontWeight: 600 }}>
                  Current status: No opt-out action is needed because we do not engage in profiling for significant decisions.
                </p>
              </div>
            </div>

            {/* Device Permissions */}
            <div className="nested-box">
              <p className="important lg">Device Permissions Management:</p>
              <p>
                You can revoke location, WiFi, and network permissions at any time through your device settings:
              </p>

              <div className="stack lg">
                <div className="callout">
                  <p className="important">📱 Android Users:</p>
                  <ol className="num">
                    <li>Open <strong>Settings</strong> → <strong>Apps</strong></li>
                    <li>Select <strong>SmartEco</strong></li>
                    <li>Tap <strong>Permissions</strong></li>
                    <li>Toggle off <strong>Location</strong> or <strong>Nearby WiFi networks</strong></li>
                  </ol>
                </div>

                <div className="callout">
                  <p className="important">🍎 iOS Users:</p>
                  <ol className="num">
                    <li>Open <strong>Settings</strong></li>
                    <li>Scroll down and select <strong>SmartEco</strong></li>
                    <li>Tap <strong>Location</strong> and choose <strong>Never</strong></li>
                    <li>Toggle off <strong>Local Network</strong> if prompted</li>
                  </ol>
                </div>
              </div>
            </div>

            <div className="nested-box">
              <p className="important lg">How to Opt-Out of All Data Processing:</p>
              <p>To completely stop all data collection and processing:</p>
              <ol className="num">
                <li><strong>Close your account:</strong> Email smartecosupport@ispatialtec.com with &quot;Close My Account&quot;</li>
                <li><strong>Request data deletion:</strong> We will delete your data within 30 days</li>
                <li><strong>Uninstall the app:</strong> Remove SmartEco from your device</li>
              </ol>
            </div>

            {/* Summary Table */}
            <div className="nested-box">
              <p className="summary-title md">📋 Quick Reference - Your Opt-Out Rights</p>
              <div className="table-wrapper">
                <table className="summary-table">
                  <thead>
                    <tr>
                      <th>Your Right</th>
                      <th>SmartEco Practice</th>
                      <th>Action Needed</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="bold">Opt-out of data sale (CCPA/VCDPA)</td>
                      <td className="ok">✓ We do NOT sell data</td>
                      <td className="ok">None required - Email us to confirm</td>
                    </tr>
                    <tr>
                      <td className="bold">Opt-out of targeted advertising (VCDPA)</td>
                      <td className="ok">✓ We do NOT use targeted ads</td>
                      <td className="ok">None required - Email us if practices change</td>
                    </tr>
                    <tr>
                      <td className="bold">Opt-out of profiling (VCDPA)</td>
                      <td className="ok">✓ We do NOT use profiling for legal decisions</td>
                      <td className="ok">None required - Email us if practices change</td>
                    </tr>
                    <tr>
                      <td>Opt-out of data sharing for marketing</td>
                      <td className="ok">✓ We do NOT share for marketing</td>
                      <td>None required</td>
                    </tr>
                    <tr>
                      <td>Revoke location permission</td>
                      <td>Used with your consent</td>
                      <td className="action">Change in device settings (see above)</td>
                    </tr>
                    <tr>
                      <td>Delete all personal data</td>
                      <td>Stored for service delivery</td>
                      <td className="action">Email: &quot;Delete My Data&quot;</td>
                    </tr>
                    <tr>
                      <td>Close account completely</td>
                      <td>Active until you request closure</td>
                      <td className="action">Email: &quot;Close My Account&quot;</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="table-meta">
                Contact us at <a href="mailto:smartecosupport@ispatialtec.com">smartecosupport@ispatialtec.com</a> for any questions about your privacy rights.
              </p>
            </div>
          </section>

          {/* Legal Basis */}
          <section className="section">
            <h2>8. Legal Basis for Processing Data (GDPR)</h2>
            <p>Under GDPR, we process your personal data based on the following legal grounds:</p>

            <div className="stack">
              <div className="info-box">
                <p className="label">Consent (Article 6(1)(a))</p>
                <p>For location data, WiFi network lists, and nearby hotspot information - collected only when you explicitly grant permission.</p>
              </div>

              <div className="info-box">
                <p className="label">Contractual Necessity (Article 6(1)(b))</p>
                <p>To provide air quality monitoring services, device management, and dashboard access as part of our service agreement with you.</p>
              </div>

              <div className="info-box">
                <p className="label">Legitimate Interest (Article 6(1)(f))</p>
                <p>To improve service quality, troubleshoot issues, and ensure platform security.</p>
              </div>

              <div className="info-box">
                <p className="label">Legal Obligation (Article 6(1)(c))</p>
                <p>To comply with applicable laws and regulations.</p>
              </div>
            </div>
          </section>

          {/* Children's Privacy */}
          <section className="section">
            <h2>9. Children&apos;s Privacy</h2>
            <p>
              SmartEco is not intended for use by children under the age of <strong>13 years</strong> (or 16 in the European Union).
            </p>
            <p>
              We do not knowingly collect personal information from children. If you believe a child has provided us with personal data, please contact us immediately at smartecosupport@ispatialtec.com, and we will delete it promptly.
            </p>
          </section>

          {/* Security */}
          <section className="section">
            <h2>
              <Lock />
              10. Data Security
            </h2>
            <p>We implement appropriate technical and organizational security measures to protect your personal data:</p>
            <ul className="dot spaced">
              <li>All data transmissions are encrypted using <strong>HTTPS/TLS</strong></li>
              <li>Passwords are encrypted using industry-standard hashing algorithms</li>
              <li>Access to personal data is restricted to authorized personnel only</li>
              <li>Regular security audits and vulnerability assessments</li>
              <li>Secure cloud infrastructure with backup and disaster recovery</li>
            </ul>
            <p>
              However, no method of transmission over the internet is 100% secure. While we strive to protect your data, we cannot guarantee absolute security.
            </p>
          </section>

          {/* Cookies */}
          <section className="section">
            <h2>11. Cookies and Tracking</h2>
            <p>
              The SmartEco mobile app itself does <strong>NOT</strong> use cookies or tracking technologies.
            </p>
            <p>The web dashboard (displayed inside the app) may use essential cookies for:</p>
            <ul className="dot">
              <li>Session management and authentication</li>
              <li>User preferences and settings</li>
              <li>Security and fraud prevention</li>
            </ul>
            <p>
              We do <strong>NOT</strong> use cookies for advertising, tracking, or analytics purposes.
            </p>
          </section>

          {/* International Transfers */}
          <section className="section">
            <h2>
              <Globe />
              12. International Data Transfers
            </h2>
            <p>
              Your personal data may be transferred to and processed in countries other than your country of residence, including the United States and European Union.
            </p>
            <p>When we transfer data internationally, we ensure appropriate safeguards are in place, including:</p>
            <ul className="dot">
              <li>Standard Contractual Clauses (SCCs) approved by the European Commission</li>
              <li>Adequacy decisions by regulatory authorities</li>
              <li>Data processing agreements with service providers</li>
            </ul>
          </section>

          {/* Changes to Policy */}
          <section className="section">
            <h2>13. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements.
            </p>
            <p>When we make material changes, we will:</p>
            <ul className="dot">
              <li>Update the &quot;Last Updated&quot; date at the top of this policy</li>
              <li>Notify you via email or in-app notification</li>
              <li>Provide at least <strong>30 days notice</strong> before the new policy takes effect</li>
            </ul>
            <p>
              Updates will be posted at: <a href={`${window.location.origin}${process.env.PUBLIC_URL}/privacy`} target="_blank" rel="noopener noreferrer">{`${window.location.origin}${process.env.PUBLIC_URL}/privacy`}</a>
            </p>
          </section>

          {/* Contact Us */}
          <section className="section">
            <h2>14. Contact Us</h2>
            <p>
              If you have questions, concerns, or wish to exercise your privacy rights, contact us:
            </p>
            <div className="contact-block">
              <p className="name">iSpatial Techno Solutions LLC</p>
              <p>
                <strong>Email:</strong> <a href="mailto:smartecosupport@ispatialtec.com">smartecosupport@ispatialtec.com</a><br />
                <strong>Address:</strong> iSpatial Techno Solutions LLC, Abu Dhabi, UAE<br />
                <strong>Phone:</strong> +971 50 582 5503<br />
                <strong>DPO Email:</strong> <a href="mailto:smartecosupport@ispatialtec.com">smartecosupport@ispatialtec.com</a>
              </p>
            </div>
          </section>

          {/* Compliance Overview */}
          <div className="compliance">
            <h3>
              <Shield />
              Compliance Overview
            </h3>
            <p style={{ margin: '0 0 1rem 0' }}>This Privacy Policy is fully compliant with:</p>
            <div className="compliance-grid">
              <div className="compliance-cell">✓ GDPR (EU)</div>
              <div className="compliance-cell">✓ CCPA (California)</div>
              <div className="compliance-cell">✓ VCDPA (Virginia)</div>
              <div className="compliance-cell">✓ POPIA (South Africa)</div>
              <div className="compliance-cell">✓ PIPA (South Korea)</div>
              <div className="compliance-cell">✓ Google Play Policy</div>
            </div>
          </div>

          {/* Footer */}
          <div className="page-footer">
            <div className="page-footer-row">
              <Link to="/terms" className="page-footer-link">
                View Terms of Use
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
